// Transformation functions for converting between frontend and backend forecast data structures

import { ForecastProject, CreateForecastRequest, ForecastResult } from '@/types/forecast';
import { FailureFilters } from '@/types/filters';
import {
  BackendNewForecast,
  BackendUpdateForecast,
  BackendForecastListResponse,
  ensureBackendForecast
} from '@/types/backend';

/**
 * Transform frontend ForecastProject to backend NewForecast format
 */
export function transformForecastToBackend(
  workspace: ForecastProject | CreateForecastRequest,
  userId: string
): BackendNewForecast {
  // Handle both ForecastProject and CreateForecastRequest types
  const isFullWorkspace = 'id' in workspace;
  
  // Package all frontend forecast data into the forecast_data JSON field
  const forecastData = {
    configuration: workspace.configuration,
    weibullParameters: workspace.weibullParameters,
    results: 'results' in workspace ? workspace.results : [],
    sourceAnalysisWorkspaceId: workspace.sourceAnalysisWorkspaceId,
    sourceAnalysisName: 'sourceAnalysisName' in workspace ? workspace.sourceAnalysisName : ''
  };

  return {
    forecast_name: workspace.name,
    description: workspace.description || '',
    workspace_id: workspace.sourceAnalysisWorkspaceId, // Associated workspace ID
    tags: isFullWorkspace && 'metadata' in workspace ? workspace.metadata?.tags || [] : [],
    owner: userId, // User ID as owner
    forecast_data: JSON.stringify(forecastData) // Backend expects JSON string
  };
}

/**
 * Transform backend forecast response to frontend ForecastProject format
 */
export function transformForecastFromBackend(backendForecast: unknown): ForecastProject {
  const forecast = ensureBackendForecast(backendForecast);
  // Parse the forecast_data JSON field
  let forecastData: Record<string, unknown> = {};
  
  try {
    if (typeof forecast.forecast_data === 'string') {
      forecastData = JSON.parse(forecast.forecast_data) as Record<string, unknown>;
    } else if (forecast.forecast_data && typeof forecast.forecast_data === 'object') {
      forecastData = forecast.forecast_data as Record<string, unknown>;
    } else {
      forecastData = {};
    }
  } catch (error) {
    console.error('Error parsing forecast_data:', error);
    forecastData = {};
  }

  return {
    id: forecast.forecast_id,
    name: forecast.forecast_name,
    description: forecast.description,
    sourceAnalysisWorkspaceId: (forecastData.sourceAnalysisWorkspaceId as string) || forecast.associated_workspace_id || forecast.workspace_id || '',
    sourceAnalysisName: (forecastData.sourceAnalysisName as string) || '',
    
    configuration: (forecastData.configuration as ForecastProject['configuration']) || {
      filters: {} as FailureFilters,
      forecastParams: {
        horizonYears: 5,
        granularityMonths: 1,
        startDate: new Date().toISOString()
      }
    },
    
    weibullParameters: (forecastData.weibullParameters as ForecastProject['weibullParameters']) || {
      beta: 0,
      eta: 0,
      rSquared: 0,
      confidenceLevel: 0.95
    },
    
    results: (forecastData.results as ForecastResult[]) || [],
    
    metadata: {
      userId: forecast.owner,
      createdAt: forecast.created_at || forecast.forecast_created_at || new Date().toISOString(),
      updatedAt: forecast.updated_at || forecast.forecast_updated_at || new Date().toISOString(),
      version: forecast.version || 1,
      tags: forecast.tags
    }
  };
}

/**
 * Transform update request for backend
 */
export function transformForecastUpdateToBackend(
  id: string,
  updates: Partial<ForecastProject>,
  userId: string
): BackendUpdateForecast {
  // Package updated data into forecast_data JSON field
  const forecastData: Record<string, unknown> = {};
  
  if (updates.configuration) {
    forecastData.configuration = updates.configuration;
  }
  
  if (updates.weibullParameters) {
    forecastData.weibullParameters = updates.weibullParameters;
  }
  
  if (updates.results) {
    forecastData.results = updates.results;
  }
  
  if (updates.sourceAnalysisWorkspaceId) {
    forecastData.sourceAnalysisWorkspaceId = updates.sourceAnalysisWorkspaceId;
  }
  
  if (updates.sourceAnalysisName) {
    forecastData.sourceAnalysisName = updates.sourceAnalysisName;
  }

  const backendUpdate: Partial<BackendUpdateForecast> & { forecast_id: string; updated_by: string } = {
    forecast_id: id,
    updated_by: userId
  };
  
  if (updates.name) {
    backendUpdate.forecast_name = updates.name;
  }
  
  if (updates.description !== undefined) {
    backendUpdate.description = updates.description;
  }
  
  if (updates.metadata?.tags) {
    backendUpdate.tags = updates.metadata.tags;
  }
  
  // Only include forecast_data if there are actual data updates
  if (Object.keys(forecastData).length > 0) {
    backendUpdate.forecast_data = JSON.stringify(forecastData);
  }
  
  return backendUpdate;
}

/**
 * Transform list response from backend
 */
export function transformForecastListFromBackend(backendResponse: unknown): {
  forecasts: ForecastProject[];
  total: number;
} {
  const response = backendResponse as Partial<BackendForecastListResponse>;
  const forecasts = (response.forecasts || []).map(transformForecastFromBackend);
  
  return {
    forecasts,
    total: response.total_count || response.total || forecasts.length
  };
}